import { US_TIMEZONES, WorkspaceSettingsFormSchema } from '@fundline/shared/schemas/workspace';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';
import { Alert } from '../../components/ui/Alert.tsx';
import { Button } from '../../components/ui/Button.tsx';
import { SelectField, TextField } from '../../components/ui/Field.tsx';
import { Card, PageHeader, Spinner } from '../../components/ui/Layout.tsx';
import { useWorkspaceClaims } from '../../lib/auth.tsx';
import { queryKeys } from '../../lib/queryKeys.ts';
import { supabase } from '../../lib/supabase.ts';
import { type Member, useMembersQuery } from '../members/use-members.ts';
import { SettingsTabs } from './SettingsTabs.tsx';
import { useWorkspaceQuery, type WorkspaceDetails } from './use-workspace.ts';

export function WorkspaceSettingsPage() {
  const { workspaceId } = useWorkspaceClaims();
  const workspace = useWorkspaceQuery(workspaceId);
  const members = useMembersQuery(workspaceId);

  return (
    <div className="flex flex-col gap-5">
      <PageHeader title="Workspace settings" description="Changes are recorded in the audit log." />
      <SettingsTabs />
      {workspace.isPending || members.isPending ? <Spinner /> : null}
      {workspace.isError ? <Alert tone="error">{workspace.error.message}</Alert> : null}
      {members.isError ? <Alert tone="error">{members.error.message}</Alert> : null}
      {workspace.data && members.data ? (
        <>
          <SettingsForm workspace={workspace.data} members={members.data} />
          <Card title="Account details">
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 callout">
              <dt className="text-ink-muted">Address</dt>
              <dd className="font-mono">{workspace.data.slug}</dd>
              <dt className="text-ink-muted">Plan</dt>
              <dd className="capitalize">{workspace.data.plan}</dd>
              <dt className="text-ink-muted">Texting</dt>
              <dd>
                {workspace.data.sending_enabled
                  ? 'Enabled'
                  : (workspace.data.sending_blocked_reason ??
                    'Off until 10DLC registration is approved.')}
              </dd>
            </dl>
          </Card>
        </>
      ) : null}
    </div>
  );
}

function SettingsForm({ workspace, members }: { workspace: WorkspaceDetails; members: Member[] }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(workspace.name);
  const [timezone, setTimezone] = useState(workspace.timezone);
  const [assignee, setAssignee] = useState(workspace.default_assignee_user_id ?? '');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const active = members.filter((m) => m.status === 'active');
  const assigneeOptions = [
    ...(assignee ? [] : [{ value: '', label: 'Choose a member' }]),
    ...active.map((m) => ({ value: m.userId, label: `${m.fullName || m.email} (${m.role})` })),
  ];
  const zoneOptions = US_TIMEZONES.some((z) => z.value === timezone)
    ? US_TIMEZONES
    : [{ value: timezone, label: timezone }, ...US_TIMEZONES];

  const save = useMutation({
    mutationFn: async (values: {
      name: string;
      timezone: string;
      defaultAssigneeUserId: string;
    }) => {
      const { error } = await supabase()
        .from('workspaces')
        .update({
          name: values.name,
          timezone: values.timezone,
          default_assignee_user_id: values.defaultAssigneeUserId,
        })
        .eq('id', workspace.id);
      if (error) throw new Error(`Couldn't save: ${error.message}`);
    },
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: queryKeys.workspace(workspace.id) }),
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    const parsed = WorkspaceSettingsFormSchema.safeParse({
      name,
      timezone,
      defaultAssigneeUserId: assignee,
    });
    if (!parsed.success) {
      setErrors(Object.fromEntries(parsed.error.issues.map((i) => [String(i.path[0]), i.message])));
      return;
    }
    setErrors({});
    save.mutate(parsed.data);
  }

  return (
    <Card title="General">
      <form className="flex flex-col gap-4" onSubmit={onSubmit} noValidate>
        <TextField
          label="Workspace name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          error={errors.name}
        />
        <SelectField
          label="Time zone"
          hint="Used for reporting days. Texts always follow each merchant's own time zone."
          value={timezone}
          onChange={(e) => setTimezone(e.target.value)}
          options={zoneOptions}
          error={errors.timezone}
        />
        <SelectField
          label="Default assignee"
          hint="Gets new merchants that text in from unknown numbers or arrive without a rep."
          value={assignee}
          onChange={(e) => setAssignee(e.target.value)}
          options={assigneeOptions}
          error={errors.defaultAssigneeUserId}
        />
        {save.isError ? <Alert tone="error">{save.error.message}</Alert> : null}
        {save.isSuccess ? <Alert tone="success">Settings saved.</Alert> : null}
        <div>
          <Button type="submit" busy={save.isPending}>
            Save settings
          </Button>
        </div>
      </form>
    </Card>
  );
}
